/**
 * KAN-761: товар каталога в заказ клиента.
 * open(product) открывает диалог, submit(client, quantity):
 * POST /api/v1/orders/ {client_id} (текущая корзина клиента, идемпотентно;
 * 409: корзину ведёт другой оператор), затем
 * PATCH /api/v1/orders/<id>/items/ {op:"add", product_id, quantity}.
 * Успех: обновляются заказы и доска CRM (refreshAfterOrderChange), диалог
 * показывает "Добавлено в корзину" с кнопкой "Открыть корзину", тост с тем
 * же действием. openCart закрывает диалог и открывает формирование заказа
 * (OrderComposeDialog по composeOrderId).
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { refreshAfterOrderChange } from '@/hooks/useOrders';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { ordersApi } from '@/services/api/orders';
import type { CatalogProduct } from '@/types/catalog';
import type { Client } from '@/types/client';

const ERROR_FALLBACK = 'Не удалось добавить товар в заказ';

/** Товар в продаже: в наличии и не снят с фида. */
export function canOrderProduct(product: Pick<CatalogProduct, 'available' | 'delisted_at'>): boolean {
  return product.available && !product.delisted_at;
}

const QUANTITY_RE = /^\d+$/;

/** Количество из поля: целое >= 1 или null. */
export function parseOrderQuantity(value: string): number | null {
  const text = value.trim();
  if (!QUANTITY_RE.test(text)) return null;
  const n = Number(text);
  return n >= 1 ? n : null;
}

/** Имя клиента для текста: имя, компания или телефон. */
export function clientDisplayName(client: Pick<Client, 'name' | 'company' | 'phone'>): string {
  return client.name || client.company || client.phone || 'Клиент';
}

export interface AddedToOrder {
  orderId: string;
  clientName: string;
}

interface AddVars {
  client: Client;
  product: CatalogProduct;
  quantity: number;
}

export function useAddToOrder() {
  const queryClient = useQueryClient();
  const [product, setProduct] = useState<CatalogProduct | null>(null);
  const [added, setAdded] = useState<AddedToOrder | null>(null);
  const [composeOrderId, setComposeOrderId] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async ({ client, product: target, quantity }: AddVars) => {
      const cart = await ordersApi.create(client.id);
      return ordersApi.patchItems(cart.id, { op: 'add', product_id: target.id, quantity });
    },
    onSuccess: (order) => refreshAfterOrderChange(queryClient, order),
  });
  const { reset } = mutation;

  const close = () => {
    setProduct(null);
    setAdded(null);
    reset();
  };

  const openCart = (orderId: string) => {
    close();
    setComposeOrderId(orderId);
  };

  const submit = (client: Client, quantity: number) => {
    if (!product || mutation.isPending) return;
    mutation.mutate(
      { client, product, quantity },
      {
        onSuccess: (order) => {
          const clientName = clientDisplayName(client);
          setAdded({ orderId: order.id, clientName });
          toast.success(`Добавлено в корзину: ${clientName}`, {
            action: { label: 'Открыть корзину', onClick: () => openCart(order.id) },
          });
        },
      }
    );
  };

  return {
    /** Товар открытого диалога; null: диалог закрыт. */
    product,
    open: (next: CatalogProduct) => {
      reset();
      setAdded(null);
      setProduct(next);
    },
    close,
    submit,
    pending: mutation.isPending,
    error: mutation.error ? extractErrorMessage(mutation.error, ERROR_FALLBACK) : null,
    /** Товар добавлен: заказ и клиент для кнопки "Открыть корзину". */
    added,
    openCart,
    composeOrderId,
    closeCompose: () => setComposeOrderId(null),
  };
}

export type AddToOrderFlow = ReturnType<typeof useAddToOrder>;
